import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/http';
import { AdminPageHeader } from '@/components/admin/admin-shell';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/admin/avaliacoes')({
  component: AdminAvaliacoes,
});

type Review = {
  id: string;
  rating: number;
  comment: string | null;
  therapistReply: string | null;
  isPublished: boolean;
  createdAt: string;
  client: { user: { name: string; email: string } };
  therapist: { user: { name: string } };
  session: { id: string; scheduledAt: string };
};

function AdminAvaliacoes() {
  const qc = useQueryClient();
  const { data: reviews = [], isLoading } = useQuery({
    queryKey: ['admin-reviews'],
    queryFn: () => api.get('admin/reviews').json<Review[]>(),
  });

  const setPublished = useMutation({
    mutationFn: (vars: { id: string; isPublished: boolean }) =>
      api.patch(`admin/reviews/${vars.id}/publish`, { json: { isPublished: vars.isPublished } }).json<any>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-reviews'] }),
  });

  return (
    <>
      <AdminPageHeader title="Avaliações" subtitle="Notas e respostas dos clientes" />
      {isLoading ? (
        <div className="bg-white rounded-3xl p-12 text-center border border-slate-100">
          <p className="text-slate-400 text-sm">Carregando...</p>
        </div>
      ) : reviews.length === 0 ? (
        <div className="bg-white rounded-3xl p-12 text-center border border-slate-100 shadow-sm">
          <p className="text-slate-500">Nenhuma avaliação registrada ainda.</p>
        </div>
      ) : (
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm divide-y divide-slate-100">
          {reviews.map((r) => (
            <div key={r.id} className="p-5">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="font-bold text-slate-700">
                    {r.client?.user?.name}{' '}
                    <span className="text-slate-400 font-normal">→</span>{' '}
                    <span className="text-slate-600">{r.therapist?.user?.name}</span>
                  </p>
                  <p className="text-xs text-slate-400">
                    {new Date(r.createdAt).toLocaleDateString('pt-BR')} ·{' '}
                    Sessão de {new Date(r.session.scheduledAt).toLocaleDateString('pt-BR')}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-amber-400 text-base whitespace-nowrap">
                    {'★'.repeat(r.rating)}
                    <span className="text-slate-200">{'★'.repeat(5 - r.rating)}</span>
                  </span>
                  <span className={cn(
                    'text-[10px] px-2 py-0.5 rounded border whitespace-nowrap',
                    r.isPublished ? 'bg-teal-50 text-teal-700 border-teal-200' : 'bg-slate-50 text-slate-500 border-slate-200',
                  )}>
                    {r.isPublished ? 'Pública' : 'Oculta'}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    disabled={setPublished.isPending}
                    onClick={() => setPublished.mutate({ id: r.id, isPublished: !r.isPublished })}
                  >
                    {r.isPublished ? 'Ocultar' : 'Publicar'}
                  </Button>
                </div>
              </div>
              {r.comment && (
                <p className="mt-3 text-sm text-slate-600 whitespace-pre-line">{r.comment}</p>
              )}
              {r.therapistReply && (
                <div className="mt-3 bg-teal-50 border-l-2 border-teal-300 pl-3 py-2">
                  <p className="text-[10px] uppercase tracking-wide text-teal-600 font-semibold mb-1">
                    Resposta do profissional
                  </p>
                  <p className="text-sm text-slate-700 whitespace-pre-line">{r.therapistReply}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
}
